/**
 * Subscription Management Page (Super Admin)
 * 전체 독서실 구독 관리 페이지
 * - 탭 구조: 전체 목록, 만료 임박, 미결제, 결제 대기
 * - 통계 카드 with 알림 배지
 * - 결제 대기 승인/거부 기능
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useOrganizationSubscriptions } from '../../hooks/useOrganizationSubscriptions';
import {
  OrganizationSubscription,
  SubscriptionAlertStats,
  ExpiringOrganization,
  UnpaidOrganization,
  PendingPlanRequest,
  SubscriptionAdminRepository,
} from '../../repositories/SubscriptionAdminRepository';
import { API_BASE_URL } from '../../config/api';
import { getTodayKST } from '../../utils/dateUtils';

// 플랜 타입 정의
interface ServicePlan {
  id: string;
  name: string;
  display_name: string;
  price: number;
  billing_cycle: string;
  limits: {
    max_students: number;
    max_gates: number;
    max_branches: number;
  };
  is_active: boolean;
}

// 탭 타입 정의
type TabType = 'all' | 'unpaid' | 'pending' | 'payments';

// 입금 이력 타입
interface PaymentRecord {
  id: string;
  organization_id: string;
  organization_name?: string;
  year_month: string;
  payment_type: string;
  amount: number;
  notes: string | null;
  paid_at: string;
  created_at: string;
}

// 결제 요약 타입
interface PaymentSummary {
  total_billed: number;
  total_paid: number;
  outstanding: number;
  is_paid: boolean;
}

// 월별 청구 이력 타입
interface BillingMonth {
  year_month: string;
  base_fee: number;
  alimtalk_used: number;
  alimtalk_free: number;
  alimtalk_over: number;
  alimtalk_cost: number;
  subtotal: number;
  vat: number;
  billed: number;      // VAT 포함 총 청구
  paid: number;
  carry_in: number;    // 이전 달에서 이월된 초과 입금
  outstanding: number;  // VAT 포함 잔여
  is_paid: boolean;
}

// 미납 월 타입
interface OutstandingMonth {
  year_month: string;
  billed: number;
  paid: number;
  outstanding: number;
}

// Repository 인스턴스
const repository = new SubscriptionAdminRepository();

export const SubscriptionManagementPage: React.FC = () => {
  const {
    items,
    loading,
    error,
    calculateStats,
    getDaysRemaining,
    updatePlan,
    getDetail,
    fetchAll,
  } = useOrganizationSubscriptions();

  // 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('all');

  // 필터 상태
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSubscription | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [usageData, setUsageData] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // 플랜 목록 상태
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // 할인 관련 상태
  const [discountMode, setDiscountMode] = useState<'rate' | 'amount'>('rate');
  const [discountRate, setDiscountRate] = useState<number>(0);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState<number>(0);

  // 시작일 상태 (기본값: 오늘)
  const [startDate, setStartDate] = useState<string>(
    getTodayKST()
  );

  // 현재 설정값 상태
  const [currentSettings, setCurrentSettings] = useState<any>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // 알림톡 무료 제공량 상태
  const [alimtalkFreeQuota, setAlimtalkFreeQuota] = useState<number | null>(null);

  // 과금 방식 상태
  const [billingType, setBillingType] = useState<'prepaid' | 'postpaid'>('postpaid');

  // 알림톡 사용량 데이터 (조직별)
  const [alimtalkUsageByOrg, setAlimtalkUsageByOrg] = useState<Record<string, {
    success_count: number;
    free_quota: number;
    excess_count: number;
    excess_cost: number;
    usage_percentage: number;
    is_partial_month?: boolean;
    full_free_quota?: number;
  }>>({});
  const [alimtalkUsageLoading, setAlimtalkUsageLoading] = useState(false);

  // 선택된 기간 상태
  const [selectedMonths, setSelectedMonths] = useState<number>(12);

  // 선택된 플랜 상태
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  // 계산 미리보기 표시 상태
  const [showCalculation, setShowCalculation] = useState(false);

  // ============================================================
  // 구독 알림 관련 상태 (새 API 연동)
  // ============================================================
  const [alertStats, setAlertStats] = useState<SubscriptionAlertStats | null>(null);
  const [alertStatsLoading, setAlertStatsLoading] = useState(false);
  const [expiringOrgs, setExpiringOrgs] = useState<ExpiringOrganization[]>([]);
  const [expiringLoading, setExpiringLoading] = useState(false);
  const [unpaidOrgs, setUnpaidOrgs] = useState<UnpaidOrganization[]>([]);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<PendingPlanRequest[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // 입금 이력 상태 (전체 탭)
  const [allPayments, setAllPayments] = useState<PaymentRecord[]>([]);
  const [allPaymentsLoading, setAllPaymentsLoading] = useState(false);
  const [allPaymentsTotalCount, setAllPaymentsTotalCount] = useState(0);
  const [allPaymentsPage, setAllPaymentsPage] = useState(0);
  const [allPaymentsMonthFilter, setAllPaymentsMonthFilter] = useState<string>('');

  // 입금 이력 상태 (상세 모달)
  const [modalPayments, setModalPayments] = useState<PaymentRecord[]>([]);
  const [modalOutstanding, setModalOutstanding] = useState<{ total_outstanding: number; months_with_outstanding: OutstandingMonth[] } | null>(null);
  const [modalBillingHistory, setModalBillingHistory] = useState<BillingMonth[]>([]);
  const [modalPaymentsLoading, setModalPaymentsLoading] = useState(false);

  // 승인/거부 모달 상태
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<PendingPlanRequest | null>(null);
  const [confirmStartDate, setConfirmStartDate] = useState<string>(
    getTodayKST()
  );
  const [confirmMonths, setConfirmMonths] = useState<number>(12);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [processingRequest, setProcessingRequest] = useState(false);

  // 동기화 API 키 모달 상태
  const [showSyncKeyModal, setShowSyncKeyModal] = useState(false);
  const [syncKeyOrg, setSyncKeyOrg] = useState<OrganizationSubscription | null>(null);
  const [syncKeyData, setSyncKeyData] = useState<{ sync_api_key: string | null; has_key: boolean; cloud_sync_connected?: boolean; cloud_sync_enabled?: boolean } | null>(null);
  const [syncKeyLoading, setSyncKeyLoading] = useState(false);
  const [localServerUrl, setLocalServerUrl] = useState('');
  const [pullLoading, setPullLoading] = useState(false);
  const [pullResult, setPullResult] = useState<string | null>(null);

  // 플랜 목록 조회
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        // Admin 로그인은 admin_token 또는 token 사용 (SubscriptionAdminRepository와 동일)
        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/v1/admin/service-plans/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setPlans(data.plans || []);
          // 첫 번째 플랜 가격 설정
          if (data.plans && data.plans.length > 0) {
            setSelectedPlanPrice(data.plans[0].price);
          }
        } else {
          console.error('플랜 조회 실패:', response.status, response.statusText);
        }
      } catch (err) {
        console.error('플랜 조회 실패:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // 알림 통계 조회
  const fetchAlertStats = useCallback(async () => {
    setAlertStatsLoading(true);
    try {
      const stats = await repository.getAlertStats();
      setAlertStats(stats);
    } catch (err) {
      console.error('알림 통계 조회 실패:', err);
    } finally {
      setAlertStatsLoading(false);
    }
  }, []);

  // 알림톡 사용량 조회
  const fetchAlimtalkUsage = useCallback(async () => {
    setAlimtalkUsageLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/subscription-alerts/alimtalk-usage?year_month=${currentMonth}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAlimtalkUsageByOrg(data.usage_by_org || {});
      } else {
        console.error('알림톡 사용량 조회 실패:', response.status);
      }
    } catch (err) {
      console.error('알림톡 사용량 조회 실패:', err);
    } finally {
      setAlimtalkUsageLoading(false);
    }
  }, []);

  // 만료 임박 업체 조회
  const fetchExpiringOrgs = useCallback(async () => {
    setExpiringLoading(true);
    try {
      const result = await repository.getExpiringOrganizations(7);
      setExpiringOrgs(result.organizations);
    } catch (err) {
      console.error('만료 임박 업체 조회 실패:', err);
    } finally {
      setExpiringLoading(false);
    }
  }, []);

  // 미결제 업체 조회
  const fetchUnpaidOrgs = useCallback(async () => {
    setUnpaidLoading(true);
    try {
      const result = await repository.getUnpaidOrganizations();
      setUnpaidOrgs(result.organizations);
    } catch (err) {
      console.error('미결제 업체 조회 실패:', err);
    } finally {
      setUnpaidLoading(false);
    }
  }, []);

  // 결제 대기 신청 조회
  const fetchPendingRequests = useCallback(async () => {
    setPendingLoading(true);
    try {
      const result = await repository.getPendingRequests();
      setPendingRequests(result.requests);
    } catch (err) {
      console.error('결제 대기 신청 조회 실패:', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  // 전체 입금 이력 조회
  const fetchAllPayments = useCallback(async (page = 0, yearMonth = '') => {
    setAllPaymentsLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('limit', '20');
      params.set('offset', String(page * 20));
      if (yearMonth) params.set('year_month', yearMonth);
      const res = await fetch(`${API_BASE_URL}/api/admin/billing/payments-all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllPayments(data.payments || []);
        setAllPaymentsTotalCount(data.total_count || 0);
      }
    } catch (err) {
      console.error('전체 입금 이력 조회 실패:', err);
    } finally {
      setAllPaymentsLoading(false);
    }
  }, []);

  // 업체별 입금 이력 + 청구 이력 조회 (상세 모달용)
  const fetchModalPayments = useCallback(async (orgId: string) => {
    setModalPaymentsLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // 입금 이력 + 미납금 + 청구 이력 병렬 조회
      const [paymentsRes, outstandingRes, billingRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/billing/organizations/${orgId}/payments`, { headers }),
        fetch(`${API_BASE_URL}/api/admin/billing/organizations/${orgId}/outstanding`, { headers }),
        fetch(`${API_BASE_URL}/api/admin/billing/organizations/${orgId}/billing-history`, { headers }),
      ]);
      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setModalPayments(data.payments || []);
      }
      if (outstandingRes.ok) {
        const data = await outstandingRes.json();
        setModalOutstanding({
          total_outstanding: data.total_outstanding || 0,
          months_with_outstanding: data.months_with_outstanding || [],
        });
      }
      if (billingRes.ok) {
        const data = await billingRes.json();
        setModalBillingHistory(data.months || []);
      }
    } catch (err) {
      console.error('업체 입금/청구 이력 조회 실패:', err);
    } finally {
      setModalPaymentsLoading(false);
    }
  }, []);

  // 입금 기록 삭제
  const handleDeletePayment = useCallback(async (paymentId: string, orgId?: string) => {
    if (!window.confirm('이 입금 기록을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/admin/billing/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        alert('삭제되었습니다.');
        // 전체 탭 새로고침
        fetchAllPayments(allPaymentsPage, allPaymentsMonthFilter);
        // 모달 새로고침
        if (orgId) fetchModalPayments(orgId);
      } else {
        const err = await res.json();
        alert(`삭제 실패: ${err.detail || '알 수 없는 오류'}`);
      }
    } catch (err) {
      alert('삭제 중 오류 발생');
    }
  }, [allPaymentsPage, allPaymentsMonthFilter, fetchAllPayments, fetchModalPayments]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchAlertStats();
    fetchAlimtalkUsage();
  }, [fetchAlertStats, fetchAlimtalkUsage]);

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab === 'unpaid') {
      fetchUnpaidOrgs();
    } else if (activeTab === 'pending') {
      fetchPendingRequests();
    } else if (activeTab === 'payments') {
      fetchAllPayments(0, allPaymentsMonthFilter);
    }
  }, [activeTab, fetchExpiringOrgs, fetchUnpaidOrgs, fetchPendingRequests, fetchAllPayments, allPaymentsMonthFilter]);

  // 통계 계산 (기존 클라이언트 측 로직 유지)
  const stats = useMemo(() => calculateStats(), [calculateStats]);

  // 필터링 및 검색
  const filteredItems = useMemo(() => {
    let result = items;

    // 플랜 필터
    if (planFilter) {
      result = result.filter((org) => org.subscription_plan === planFilter);
    }

    // 상태 필터
    if (statusFilter) {
      result = result.filter((org) => org.subscription_status === statusFilter);
    }

    // 검색
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(
        (org) =>
          org.name?.toLowerCase().includes(lowerQuery) ||
          org.owner_name?.toLowerCase().includes(lowerQuery) ||
          org.email?.toLowerCase().includes(lowerQuery) ||
          org.phone?.includes(searchQuery)
      );
    }

    // 정렬: 체험 → 활성 → 만료 → 정지
    const statusOrder: Record<string, number> = {
      trial: 1,
      active: 2,
      expired: 3,
      suspended: 4,
    };
    result.sort((a, b) => {
      const orderA = statusOrder[a.subscription_status || ''] ?? 99;
      const orderB = statusOrder[b.subscription_status || ''] ?? 99;
      return orderA - orderB;
    });

    return result;
  }, [items, planFilter, statusFilter, searchQuery]);

  // 플랜 배지 색상
  const getPlanBadgeColor = (plan: string | null): string => {
    const planLower = plan?.toLowerCase();
    switch (planLower) {
      case 'local_free':
      case 'free':
        return 'bg-gray-100 text-gray-800';
      case 'cloud_sync':
        return 'bg-blue-100 text-blue-800';
      // 레거시 플랜
      case 'starter':
        return 'bg-gray-200 text-gray-800';
      case 'basic':
        return 'bg-blue-100 text-blue-800';
      case 'pro':
        return 'bg-purple-100 text-purple-800';
      case 'premium':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  // 플랜 표시명 가져오기
  const getPlanDisplayName = (planName: string | null): string => {
    if (!planName) return '-';
    // 하이브리드 플랜 매핑
    const hybridNames: Record<string, string> = {
      'LOCAL_FREE': '로컬 전용',
      'local_free': '로컬 전용',
      'CLOUD_SYNC': '클라우드 동기화',
      'cloud_sync': '클라우드 동기화',
    };
    if (hybridNames[planName]) return hybridNames[planName];
    const plan = plans.find(p => p.name.toLowerCase() === planName.toLowerCase());
    return plan?.display_name || planName;
  };

  // 상태 배지 색상
  const getStatusBadgeColor = (status: string | null): string => {
    switch (status) {
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-orange-100 text-orange-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  // 상태 텍스트
  const getStatusText = (status: string | null): string => {
    switch (status) {
      case 'trial':
        return '체험';
      case 'active':
        return '활성';
      case 'suspended':
        return '정지';
      case 'expired':
        return '만료';
      default:
        return '-';
    }
  };

  // 현재 설정값 로드 함수
  const loadCurrentSettings = async (orgId: string) => {
    setSettingsLoading(true);
    try {
      const detail = await getDetail(orgId);
      if (detail && detail.current_settings) {
        const settings = detail.current_settings;
        setCurrentSettings(settings);

        // 현재 값으로 모든 폼 필드 초기화 (저장 시 기존값 유지되도록)
        if (settings.plan_name) {
          setSelectedPlan(settings.plan_name);
        }
        if (settings.original_price) {
          setSelectedPlanPrice(settings.original_price);
        }
        if (settings.start_date) {
          setStartDate(settings.start_date);
        }

        // 요금 설정: 할인율 또는 월 요금 직접입력 복원
        const hasDiscount = settings.discount_rate && settings.discount_rate > 0;
        const hasCustomFee = settings.monthly_fee && settings.monthly_fee > 0 &&
          settings.original_price && settings.monthly_fee !== settings.original_price;

        if (hasCustomFee) {
          // 커스텀 월 요금이 플랜 가격과 다르면 → 직접입력 모드
          setPaidAmount(settings.monthly_fee);
          setDiscountMode('amount');
          setDiscountRate(0);
        } else if (hasDiscount) {
          // 할인율이 있으면 → 할인율 모드
          setDiscountRate(settings.discount_rate);
          setDiscountMode('rate');
          setPaidAmount(0);
        } else {
          // 기본값 (할인 없음)
          setDiscountRate(0);
          setDiscountMode('rate');
          setPaidAmount(0);
        }

        // 알림톡 무료 제공량 복원
        if (settings.custom_alimtalk_free_quota !== null && settings.custom_alimtalk_free_quota !== undefined) {
          setAlimtalkFreeQuota(settings.custom_alimtalk_free_quota);
        } else {
          // 커스텀 값이 없으면 플랜 기본값 사용
          setAlimtalkFreeQuota(settings.default_alimtalk_free_quota ?? null);
        }

        // 과금 방식 복원
        setBillingType(settings.billing_type || 'postpaid');
      }
    } catch (err) {
      console.error('설정값 로드 실패:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  // 플랜 업데이트 핸들러
  const handleUpdatePlan = async (
    orgId: string,
    plan: 'starter' | 'basic' | 'pro' | 'premium',
    months: number,
    planStartDate: string
  ) => {
    const request: any = { plan, months, start_date: planStartDate };

    // 할인 정보 추가
    if (discountMode === 'rate' && discountRate > 0) {
      request.discount_rate = discountRate;
    } else if (discountMode === 'amount' && paidAmount > 0) {
      // 월 요금 = paid_amount (약정 없이 월 단위 과금)
      request.paid_amount = paidAmount;
    }

    // 알림톡 무료 제공량 추가
    if (alimtalkFreeQuota !== null) {
      request.custom_alimtalk_free_quota = alimtalkFreeQuota;
    }

    // 과금 방식
    request.billing_type = billingType;

    const success = await updatePlan(orgId, request);
    if (success) {
      alert('플랜이 업데이트되었습니다.');
      setShowModal(false);
      // 상태 초기화
      setDiscountRate(0);
      setPaidAmount(0);
      setDiscountMode('rate');
      setStartDate(getTodayKST());
      setCurrentSettings(null);
      setAlimtalkFreeQuota(null);
      setSelectedMonths(12);
      setSelectedPlan('');
      setShowCalculation(false);
      // 통계 새로고침
      fetchAlertStats();
    } else {
      alert('플랜 업데이트에 실패했습니다.');
    }
  };

  // 플랜 신청 승인 핸들러
  const handleConfirmRequest = async () => {
    if (!selectedRequest) return;

    setProcessingRequest(true);
    try {
      await repository.confirmPlanRequest(selectedRequest.id, {
        start_date: confirmStartDate,
        months: confirmMonths,
      });
      alert('플랜 신청이 승인되었습니다.');
      setShowConfirmModal(false);
      setSelectedRequest(null);
      setConfirmStartDate(getTodayKST());
      setConfirmMonths(12);
      // 데이터 새로고침
      fetchPendingRequests();
      fetchAlertStats();
      fetchAll();
    } catch (err) {
      console.error('플랜 신청 승인 실패:', err);
      alert('플랜 신청 승인에 실패했습니다.');
    } finally {
      setProcessingRequest(false);
    }
  };

  // 플랜 신청 거부 핸들러
  const handleRejectRequest = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      alert('거부 사유를 입력해주세요.');
      return;
    }

    setProcessingRequest(true);
    try {
      await repository.rejectPlanRequest(selectedRequest.id, {
        reason: rejectReason,
      });
      alert('플랜 신청이 거부되었습니다.');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      // 데이터 새로고침
      fetchPendingRequests();
      fetchAlertStats();
    } catch (err) {
      console.error('플랜 신청 거부 실패:', err);
      alert('플랜 신청 거부에 실패했습니다.');
    } finally {
      setProcessingRequest(false);
    }
  };

  // 새로고침 핸들러
  const handleRefresh = () => {
    fetchAlertStats();
    fetchAll();
    if (activeTab === 'unpaid') fetchUnpaidOrgs();
    if (activeTab === 'pending') fetchPendingRequests();
    if (activeTab === 'payments') fetchAllPayments(allPaymentsPage, allPaymentsMonthFilter);
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="text-gray-600">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded">
        에러: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">구독 관리</h1>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
        >
          새로고침
        </button>
      </div>

      {/* 통계 카드 (API 통계 사용) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">전체 독서실</div>
          <div className="text-3xl font-bold mt-2">
            {alertStats?.total ?? stats.total}
          </div>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg shadow">
          <div className="text-yellow-800 text-sm">체험 중 (FREE)</div>
          <div className="text-3xl font-bold mt-2 text-yellow-900">
            {alertStats?.by_status.trial ?? stats.trial}
          </div>
        </div>
        <div className="bg-green-50 p-6 rounded-lg shadow">
          <div className="text-green-800 text-sm">활성 (유료)</div>
          <div className="text-3xl font-bold mt-2 text-green-900">
            {alertStats?.by_status.active ?? stats.active}
          </div>
        </div>
        <div className="bg-orange-50 p-6 rounded-lg shadow">
          <div className="text-orange-800 text-sm">정지</div>
          <div className="text-3xl font-bold mt-2 text-orange-900">
            {alertStats?.by_status.suspended ?? 0}
          </div>
        </div>
        <div className="bg-red-50 p-6 rounded-lg shadow">
          <div className="text-red-800 text-sm">만료</div>
          <div className="text-3xl font-bold mt-2 text-red-900">
            {alertStats?.by_status.expired ?? stats.expired}
          </div>
        </div>
      </div>

      {/* 알림 배지 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setActiveTab('pending')}
          className={`p-4 rounded-lg shadow text-left transition-all ${
            activeTab === 'pending'
              ? 'bg-blue-500 text-white'
              : 'bg-white hover:bg-blue-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${activeTab === 'pending' ? 'text-blue-100' : 'text-blue-600'}`}>
                결제 대기 (입금 확인 필요)
              </div>
              <div className={`text-2xl font-bold mt-1 ${activeTab === 'pending' ? 'text-white' : 'text-blue-700'}`}>
                {alertStats?.alerts.payment_pending ?? 0}건
              </div>
            </div>
            {(alertStats?.alerts.payment_pending ?? 0) > 0 && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                activeTab === 'pending'
                  ? 'bg-white text-blue-600'
                  : 'bg-blue-500 text-white'
              }`}>
                처리 필요
              </span>
            )}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('unpaid')}
          className={`p-4 rounded-lg shadow text-left transition-all ${
            activeTab === 'unpaid'
              ? 'bg-red-500 text-white'
              : 'bg-white hover:bg-red-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${activeTab === 'unpaid' ? 'text-red-100' : 'text-red-600'}`}>
                미결제 업체
              </div>
              <div className={`text-2xl font-bold mt-1 ${activeTab === 'unpaid' ? 'text-white' : 'text-red-700'}`}>
                {alertStats?.alerts.unpaid ?? 0}건
              </div>
            </div>
            {(alertStats?.alerts.unpaid ?? 0) > 0 && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                activeTab === 'unpaid'
                  ? 'bg-white text-red-600'
                  : 'bg-red-500 text-white'
              }`}>
                긴급
              </span>
            )}
          </div>
        </button>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              전체 목록
            </button>
            <button
              onClick={() => setActiveTab('unpaid')}
              className={`py-4 px-6 text-sm font-medium border-b-2 flex items-center ${
                activeTab === 'unpaid'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              미결제 업체
              {(alertStats?.alerts.unpaid ?? 0) > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {alertStats?.alerts.unpaid}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-6 text-sm font-medium border-b-2 flex items-center ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              결제 대기
              {(alertStats?.alerts.payment_pending ?? 0) > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  {alertStats?.alerts.payment_pending}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-4 px-6 text-sm font-medium border-b-2 flex items-center ${
                activeTab === 'payments'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              입금 이력
            </button>
          </nav>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="p-6">
          {/* 전체 목록 탭 */}
          {activeTab === 'all' && (
            <>
              {/* 필터 & 검색 */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 플랜 필터 */}
                  <div>
                    <label className="block text-sm font-medium mb-2">플랜</label>
                    <select
                      value={planFilter || ''}
                      onChange={(e) => setPlanFilter(e.target.value || null)}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">전체</option>
                      <option value="FREE">FREE (체험)</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.name}>
                          {plan.display_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 상태 필터 */}
                  <div>
                    <label className="block text-sm font-medium mb-2">상태</label>
                    <select
                      value={statusFilter || ''}
                      onChange={(e) => setStatusFilter(e.target.value || null)}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">전체</option>
                      <option value="trial">체험 중</option>
                      <option value="active">활성</option>
                      <option value="suspended">정지</option>
                      <option value="expired">만료</option>
                    </select>
                  </div>

                  {/* 검색 */}
                  <div>
                    <label className="block text-sm font-medium mb-2">검색</label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="독서실명, 오너명, 이메일, 전화번호"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  총 {filteredItems.length}개의 결과
                </div>
              </div>

              {/* 전체 목록 테이블 */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        독서실명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        플랜
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        월 요금
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        알림톡 사용량
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        상태
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        시작일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        오너
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        액션
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map((org) => {
                      return (
                        <tr
                          key={org.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={async () => {
                            const detail = await getDetail(org.id);
                            if (detail) {
                              setDetailData(detail);
                              setShowDetailModal(true);
                              // 사용 현황 로드
                              setUsageData(null);
                              setUsageLoading(true);
                              try {
                                const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
                                const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${org.id}/usage`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (res.ok) setUsageData(await res.json());
                              } catch (e) { console.error('사용 현황 로드 실패:', e); }
                              finally { setUsageLoading(false); }
                              // 입금 이력 로드
                              fetchModalPayments(org.id);
                            }
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium">{org.name}</div>
                            {org.deploy_mode && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                org.deploy_mode === 'local_ubuntu'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {org.deploy_mode === 'local_ubuntu' ? 'Ubuntu' : 'Windows'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getPlanBadgeColor(
                                org.subscription_plan
                              )}`}
                            >
                              {getPlanDisplayName(org.subscription_plan)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {/* 실제 적용된 월 요금 표시 */}
                            {org.monthly_fee && org.monthly_fee > 0 ? (
                              <div>
                                <div className="text-gray-900 font-medium">
                                  {org.monthly_fee.toLocaleString()}원
                                </div>
                                {org.discount_rate && org.discount_rate > 0 && (
                                  <div className="text-xs text-green-600">
                                    ({org.discount_rate}% 할인)
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                {org.subscription_plan === 'FREE' && '무료'}
                                {org.subscription_plan === 'STARTER' && '29만원'}
                                {org.subscription_plan === 'BASIC' && '49만원'}
                                {org.subscription_plan === 'PRO' && '79만원'}
                                {org.subscription_plan === 'PREMIUM' && '99만원'}
                                {!org.subscription_plan && '-'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {/* 알림톡 사용량 표시 */}
                            {(() => {
                              const usage = alimtalkUsageByOrg[org.id];
                              if (alimtalkUsageLoading) {
                                return <span className="text-gray-400">로딩...</span>;
                              }
                              if (!usage) {
                                // 사용량 데이터 없으면 기존 방식으로 무료량만 표시
                                const freeQuota = org.custom_alimtalk_free_quota ?? (
                                  org.subscription_plan === 'FREE' ? 100 :
                                  org.subscription_plan === 'STARTER' ? 500 :
                                  org.subscription_plan === 'BASIC' ? 1000 :
                                  org.subscription_plan === 'PRO' ? 1500 :
                                  org.subscription_plan === 'PREMIUM' ? 3000 : 0
                                );
                                return (
                                  <span className="text-gray-500">
                                    0/{freeQuota.toLocaleString()}건
                                  </span>
                                );
                              }
                              const { success_count, free_quota, excess_count, excess_cost, usage_percentage } = usage;
                              const isExceeded = excess_count > 0;
                              const progressColor = usage_percentage >= 100 ? 'bg-red-500' :
                                                   usage_percentage >= 80 ? 'bg-orange-500' :
                                                   usage_percentage >= 50 ? 'bg-yellow-500' : 'bg-green-500';
                              return (
                                <div className="min-w-[120px]">
                                  <div className={`font-medium ${isExceeded ? 'text-red-600' : 'text-gray-900'}`}>
                                    {success_count.toLocaleString()}/{free_quota.toLocaleString()}건
                                    {isExceeded && ' ⚠️'}
                                  </div>
                                  {usage.is_partial_month && (
                                    <div className="text-xs text-blue-500 mt-0.5">
                                      일할 ({usage.full_free_quota?.toLocaleString()}건 기준)
                                    </div>
                                  )}
                                  {/* 프로그레스 바 */}
                                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                    <div
                                      className={`h-1.5 rounded-full ${progressColor}`}
                                      style={{ width: `${Math.min(usage_percentage, 100)}%` }}
                                    />
                                  </div>
                                  {isExceeded && (
                                    <div className="text-xs text-red-600 mt-0.5">
                                      +{excess_count.toLocaleString()}건 ({excess_cost.toLocaleString()}원)
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(
                                org.subscription_status
                              )}`}
                            >
                              {getStatusText(org.subscription_status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {org.plan_started_at
                              ? new Date(org.plan_started_at).toLocaleDateString('ko-KR')
                              : org.trial_started_at
                              ? new Date(org.trial_started_at).toLocaleDateString('ko-KR')
                              : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>{org.owner_name}</div>
                            <div className="text-gray-500 text-xs">{org.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={async () => {
                                setSelectedOrg(org);
                                setShowModal(true);
                                await loadCurrentSettings(org.id);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              플랜 변경
                            </button>
                            <button
                              onClick={async () => {
                                setSyncKeyOrg(org);
                                setSyncKeyLoading(true);
                                setShowSyncKeyModal(true);
                                try {
                                  const data = await repository.getSyncKey(org.id);
                                  setSyncKeyData(data);
                                } catch {
                                  setSyncKeyData(null);
                                } finally {
                                  setSyncKeyLoading(false);
                                }
                              }}
                              className="text-purple-600 hover:text-purple-800"
                            >
                              API 키
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredItems.length === 0 && (
                  <div className="p-12 text-center text-gray-500">
                    조회된 데이터가 없습니다.
                  </div>
                )}
              </div>
            </>
          )}

          {/* 미결제 업체 탭 */}
          {activeTab === 'unpaid' && (
            <div>
              {unpaidLoading ? (
                <div className="p-12 text-center text-gray-500">로딩 중...</div>
              ) : unpaidOrgs.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  미결제 업체가 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          독서실명
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          유형
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          플랜
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          만료일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          경과 일수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">
                          오너
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {unpaidOrgs.map((org) => (
                        <tr key={org.id} className="hover:bg-red-50">
                          <td className="px-6 py-4 whitespace-nowrap font-medium">
                            {org.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              org.type === 'trial_expired'
                                ? 'bg-yellow-100 text-yellow-800'
                                : org.type === 'plan_expired'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {org.type === 'trial_expired' && '체험 만료'}
                              {org.type === 'plan_expired' && '플랜 만료'}
                              {org.type === 'payment_pending' && '결제 대기'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getPlanBadgeColor(org.plan)}`}>
                              {getPlanDisplayName(org.plan)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {org.expired_at ? new Date(org.expired_at).toLocaleDateString('ko-KR') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-red-600">
                              +{org.days_overdue ?? 0}일
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>{org.owner_name}</div>
                            <div className="text-gray-500 text-xs">{org.owner_email}</div>
                            <div className="text-gray-500 text-xs">{org.owner_phone}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 결제 대기 탭 */}
          {activeTab === 'pending' && (
            <div>
              {pendingLoading ? (
                <div className="p-12 text-center text-gray-500">로딩 중...</div>
              ) : pendingRequests.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  결제 대기 중인 신청이 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          독서실명
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          신청 플랜
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          신청일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          입금자명
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          입금액
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          오너
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase">
                          액션
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingRequests.map((req) => (
                        <tr key={req.id} className="hover:bg-blue-50">
                          <td className="px-6 py-4 whitespace-nowrap font-medium">
                            {req.organization_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getPlanBadgeColor(req.requested_plan)}`}>
                              {getPlanDisplayName(req.requested_plan)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {new Date(req.requested_at).toLocaleDateString('ko-KR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {req.depositor_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {req.deposit_amount ? `${req.deposit_amount.toLocaleString()}원` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>{req.owner_name}</div>
                            <div className="text-gray-500 text-xs">{req.owner_email}</div>
                            <div className="text-gray-500 text-xs">{req.owner_phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                            <button
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowConfirmModal(true);
                              }}
                              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                            >
                              승인
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowRejectModal(true);
                              }}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              거부
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 입금 이력 탭 */}
          {activeTab === 'payments' && (
            <div>
              {/* 월 필터 */}
              <div className="flex items-center gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">월 선택</label>
                  <input
                    type="month"
                    value={allPaymentsMonthFilter}
                    onChange={(e) => {
                      setAllPaymentsMonthFilter(e.target.value);
                      setAllPaymentsPage(0);
                      fetchAllPayments(0, e.target.value);
                    }}
                    className="px-3 py-2 border rounded"
                  />
                </div>
                {allPaymentsMonthFilter && (
                  <button
                    onClick={() => {
                      setAllPaymentsMonthFilter('');
                      setAllPaymentsPage(0);
                      fetchAllPayments(0, '');
                    }}
                    className="mt-5 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    전체 보기
                  </button>
                )}
                <div className="mt-5 ml-auto text-sm text-gray-500">
                  총 {allPaymentsTotalCount}건
                </div>
              </div>

              {allPaymentsLoading ? (
                <div className="p-12 text-center text-gray-500">로딩 중...</div>
              ) : allPayments.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  입금 이력이 없습니다.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-emerald-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">업체명</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">청구월</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">결제 유형</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-emerald-700 uppercase">입금액</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">입금일시</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">메모</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase">액션</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allPayments.map((p) => (
                          <tr key={p.id} className="hover:bg-emerald-50">
                            <td className="px-6 py-4 whitespace-nowrap font-medium">{p.organization_name || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{p.year_month}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                p.payment_type === 'bank_transfer' ? 'bg-blue-100 text-blue-700' :
                                p.payment_type === 'card' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {p.payment_type === 'bank_transfer' ? '계좌이체' :
                                 p.payment_type === 'card' ? '카드' :
                                 p.payment_type === 'credit_offset' ? '크레딧' : p.payment_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-emerald-700">
                              {p.amount.toLocaleString()}원
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {new Date(p.paid_at).toLocaleString('ko-KR')}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                              {p.notes || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleDeletePayment(p.id)}
                                className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs"
                              >
                                삭제
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* 페이지네이션 */}
                  {allPaymentsTotalCount > 20 && (
                    <div className="flex justify-center items-center gap-4 mt-4">
                      <button
                        onClick={() => {
                          const newPage = allPaymentsPage - 1;
                          setAllPaymentsPage(newPage);
                          fetchAllPayments(newPage, allPaymentsMonthFilter);
                        }}
                        disabled={allPaymentsPage === 0}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        이전
                      </button>
                      <span className="text-sm text-gray-600">
                        {allPaymentsPage + 1} / {Math.ceil(allPaymentsTotalCount / 20)}
                      </span>
                      <button
                        onClick={() => {
                          const newPage = allPaymentsPage + 1;
                          setAllPaymentsPage(newPage);
                          fetchAllPayments(newPage, allPaymentsMonthFilter);
                        }}
                        disabled={(allPaymentsPage + 1) * 20 >= allPaymentsTotalCount}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        다음
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 상세 정보 모달 */}
      {showDetailModal && detailData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowDetailModal(false);
            setDetailData(null);
          }}
        >
          <div
            className="bg-white p-6 rounded-lg w-[650px] max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {detailData.organization_name}
              </h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setDetailData(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                X
              </button>
            </div>

            <div className="space-y-4">
              {/* 구독 상태 요약 (큰 카드) */}
              <div className={`p-4 rounded-lg border-2 ${
                detailData.subscription?.status === 'active' ? 'bg-green-50 border-green-300' :
                detailData.subscription?.status === 'suspended' ? 'bg-red-50 border-red-300' :
                detailData.subscription?.status === 'trial' ? 'bg-blue-50 border-blue-300' :
                'bg-gray-50 border-gray-300'
              }`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getPlanBadgeColor(detailData.subscription?.plan)}`}>
                        {detailData.subscription?.limits?.display_name || detailData.subscription?.plan || '-'}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(detailData.subscription?.status)}`}>
                        {getStatusText(detailData.subscription?.status)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {detailData.subscription?.ends_at
                        ? `만료: ${new Date(detailData.subscription.ends_at).toLocaleDateString('ko-KR')}`
                        : '만료일 없음'
                      }
                      {detailData.subscription?.days_remaining != null && (
                        <span className={`ml-2 font-bold ${
                          detailData.subscription.days_remaining < 0 ? 'text-red-600' :
                          detailData.subscription.days_remaining <= 7 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          ({detailData.subscription.days_remaining < 0 ? '만료됨' : `D-${detailData.subscription.days_remaining}`})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-700">
                      {!detailData.subscription?.limits?.price_monthly
                        ? 'FREE'
                        : `${detailData.subscription.limits.price_monthly.toLocaleString()}원`
                      }
                    </div>
                    <div className="text-xs text-gray-500">/월 (정가)</div>
                  </div>
                </div>
              </div>

              {/* 이번달 구독 사용 현황 */}
              {(() => {
                const orgId = detailData.organization_id;
                const listUsage = alimtalkUsageByOrg[orgId];
                const apiUsage = usageData;
                const org = items.find((o: any) => o.id === orgId);
                const now = new Date();

                // 알림톡: API 데이터 우선, 없으면 목록 데이터
                const alimtalkUsed = apiUsage?.alimtalk?.used ?? listUsage?.success_count ?? 0;
                const freeQuota = apiUsage?.alimtalk?.free_quota ?? listUsage?.free_quota ?? 0;
                const excessCount = apiUsage?.alimtalk?.over ?? listUsage?.excess_count ?? 0;
                const excessCost = apiUsage?.alimtalk?.over_cost ?? listUsage?.excess_cost ?? 0;
                const usagePct = freeQuota > 0 ? (alimtalkUsed / freeQuota * 100) : 0;

                // 월 이용료 + 일할 계산
                const fullMonthlyFee = apiUsage?.billing?.monthly_fee ?? org?.monthly_fee ?? detailData.current_settings?.monthly_fee ?? 0;
                const startDateStr = org?.plan_started_at || detailData.current_settings?.start_date;
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                let activeDays = daysInMonth;
                let isPartialMonth = false;
                if (startDateStr) {
                  const startDate = new Date(startDateStr);
                  if (startDate.getFullYear() === now.getFullYear() && startDate.getMonth() === now.getMonth()) {
                    activeDays = daysInMonth - startDate.getDate() + 1;
                    isPartialMonth = true;
                  }
                }
                const monthlyFee = isPartialMonth ? Math.round(fullMonthlyFee * activeDays / daysInMonth) : fullMonthlyFee;
                const estimatedTotal = monthlyFee + excessCost;

                // 추가 정보 (local_usage_reports 기반)
                const studentCount = apiUsage?.students ?? 0;
                const checkinCount = apiUsage?.checkins ?? 0;
                const reportDays = apiUsage?.report_days ?? 0;
                const lastReported = apiUsage?.last_reported;

                return (
                  <div className="bg-orange-50 p-4 rounded border border-orange-200">
                    <h4 className="font-medium mb-3 text-orange-800">
                      이번달 청구 현황 ({now.getFullYear()}년 {now.getMonth() + 1}월)
                      {reportDays > 0 && <span className="text-xs text-gray-500 ml-2">({reportDays}일분 리포트)</span>}
                    </h4>

                    {/* 알림톡 사용량 바 */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">알림톡 사용량</span>
                        <span className="font-bold">{alimtalkUsed.toLocaleString()} / {freeQuota.toLocaleString()}건</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className={`h-4 rounded-full transition-all ${usagePct > 100 ? 'bg-red-500' : usagePct > 80 ? 'bg-orange-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(usagePct, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-gray-500">{usagePct.toFixed(0)}% 사용</span>
                        {excessCount > 0 && (
                          <span className="text-red-600 font-bold">초과 {excessCount.toLocaleString()}건 (+{excessCost.toLocaleString()}원)</span>
                        )}
                      </div>
                    </div>

                    {/* 운영 현황 */}
                    {reportDays > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-white p-2 rounded border text-center">
                          <div className="text-lg font-bold text-green-600">{studentCount}</div>
                          <div className="text-xs text-gray-500">학생 수</div>
                        </div>
                        <div className="bg-white p-2 rounded border text-center">
                          <div className="text-lg font-bold text-blue-600">{checkinCount}</div>
                          <div className="text-xs text-gray-500">이번달 입실</div>
                        </div>
                        <div className="bg-white p-2 rounded border text-center">
                          <div className="text-lg font-bold text-gray-600">{reportDays}</div>
                          <div className="text-xs text-gray-500">운영일수</div>
                        </div>
                      </div>
                    )}

                    {/* 청구 요약 */}
                    <div className="bg-white p-3 rounded border space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          월 이용료
                          {isPartialMonth && <span className="text-xs text-orange-500 ml-1">({activeDays}/{daysInMonth}일)</span>}
                        </span>
                        <span className="font-medium">
                          {isPartialMonth && <span className="text-xs text-gray-400 line-through mr-1">{fullMonthlyFee.toLocaleString()}원</span>}
                          {monthlyFee.toLocaleString()}원
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">알림톡 초과 비용</span>
                        <span className={`font-medium ${excessCost > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {excessCost > 0 ? `+${excessCost.toLocaleString()}원` : '0원'}
                        </span>
                      </div>
                      <div className="border-t pt-2 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">소계</span>
                          <span className="font-medium">{estimatedTotal.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">VAT (10%)</span>
                          <span className="font-medium">{Math.round(estimatedTotal * 0.1).toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-sm border-t pt-1">
                          <span className="font-bold text-gray-800">예상 청구액 (VAT 포함)</span>
                          <span className="font-bold text-lg text-blue-700">{Math.round(estimatedTotal * 1.1).toLocaleString()}원</span>
                        </div>
                      </div>
                    </div>

                    {lastReported && (
                      <div className="mt-2 text-xs text-gray-500 text-right">
                        마지막 리포트: {new Date(lastReported).toLocaleString('ko-KR')}
                      </div>
                    )}
                    {reportDays === 0 && (
                      <div className="mt-2 text-xs text-orange-500 text-center">
                        리포트 데이터가 아직 없습니다 (매일 자동 수집)
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 관리 버튼들 */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setDetailData(null);
                    // 플랜 변경 모달 열기
                    const org = items.find((o: any) => o.id === detailData.organization_id);
                    if (org) {
                      setSelectedOrg(org);
                      setShowModal(true);
                      // 현재 설정 로드
                      const loadSettings = async () => {
                        setSettingsLoading(true);
                        try {
                          const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
                          const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/${org.id}/subscription`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setCurrentSettings(data.current_settings);
                          }
                        } catch (err) {
                          console.error('설정 로드 실패:', err);
                        } finally {
                          setSettingsLoading(false);
                        }
                      };
                      loadSettings();
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                >
                  플랜 변경
                </button>

                <button
                  onClick={async () => {
                    const amountStr = window.prompt('입금 금액을 입력하세요 (VAT 포함)', '');
                    if (!amountStr) return;
                    const amount = parseInt(amountStr.replace(/,/g, ''));
                    if (isNaN(amount) || amount <= 0) {
                      alert('올바른 금액을 입력해주세요');
                      return;
                    }
                    const now = new Date();
                    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const memo = window.prompt('메모 (선택)', '') || '';
                    if (!window.confirm(`${detailData.organization_name}\n${yearMonth} 입금 확인: ${amount.toLocaleString()}원 (VAT 포함)\n\n처리하시겠습니까?`)) return;
                    try {
                      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
                      const res = await fetch(`${API_BASE_URL}/api/admin/billing/organizations/${detailData.organization_id}/payments/${yearMonth}`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ payment_type: 'bank_transfer', amount, notes: memo }),
                      });
                      if (res.ok) {
                        alert(`${amount.toLocaleString()}원 입금 확인 완료`);
                        fetchModalPayments(detailData.organization_id);
                      } else {
                        const err = await res.json();
                        alert(`실패: ${err.detail || '알 수 없는 오류'}`);
                      }
                    } catch (err) {
                      alert('입금 처리 중 오류 발생');
                    }
                  }}
                  className="bg-emerald-600 text-white px-4 py-2 rounded text-sm hover:bg-emerald-700"
                >
                  입금 확인
                </button>

                {detailData.subscription?.status !== 'suspended' ? (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`'${detailData.organization_name}' 서비스를 정지하시겠습니까?`)) return;
                      try {
                        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
                        const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/${detailData.organization_id}/suspend`, {
                          method: 'POST',
                          headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({ reason: '관리자에 의한 서비스 정지' }),
                        });
                        if (response.ok) {
                          alert('서비스가 정지되었습니다.');
                          setShowDetailModal(false);
                          setDetailData(null);
                          fetchAll();
                        } else {
                          const err = await response.json();
                          alert(`실패: ${err.detail || '알 수 없는 오류'}`);
                        }
                      } catch (err) {
                        alert('서비스 정지 중 오류 발생');
                      }
                    }}
                    className="bg-red-500 text-white px-4 py-2 rounded text-sm hover:bg-red-600"
                  >
                    서비스 정지
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setDetailData(null);
                      // 재활성화 = 플랜 변경과 동일
                      const org = items.find((o: any) => o.id === detailData.organization_id);
                      if (org) {
                        setSelectedOrg(org);
                        setShowModal(true);
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                  >
                    서비스 재활성화
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setDetailData(null);
                    const org = items.find((o: any) => o.id === detailData.organization_id);
                    if (org) {
                      setSyncKeyOrg(org);
                      setShowSyncKeyModal(true);
                    }
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700"
                >
                  동기화 키
                </button>
              </div>

              {/* 입금 현황 & 이력 */}
              <div className="bg-emerald-50 p-4 rounded border border-emerald-200">
                <h4 className="font-medium mb-3 text-emerald-800">입금 현황</h4>
                {modalPaymentsLoading ? (
                  <div className="text-center text-gray-500 py-4">로딩 중...</div>
                ) : (
                  <div className="space-y-3">
                    {/* 청구/입금/잔여 요약 카드 (청구이력 기반) */}
                    {modalBillingHistory.length > 0 && (() => {
                      const totalBilled = modalBillingHistory.reduce((s, m) => s + m.billed, 0);
                      const totalPaid = modalBillingHistory.reduce((s, m) => s + m.paid, 0);
                      const totalOutstanding = modalBillingHistory.reduce((s, m) => s + m.outstanding, 0);
                      return (
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-white p-3 rounded border text-center">
                            <div className="text-xs text-gray-500">총 청구 (VAT)</div>
                            <div className="text-lg font-bold text-gray-700">
                              {totalBilled.toLocaleString()}원
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded border text-center">
                            <div className="text-xs text-gray-500">총 입금</div>
                            <div className="text-lg font-bold text-emerald-600">
                              {totalPaid.toLocaleString()}원
                            </div>
                          </div>
                          <div className={`p-3 rounded border text-center ${
                            totalOutstanding > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
                          }`}>
                            <div className="text-xs text-gray-500">잔여 (미납)</div>
                            <div className={`text-lg font-bold ${
                              totalOutstanding > 0 ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                              {totalOutstanding > 0
                                ? `${totalOutstanding.toLocaleString()}원`
                                : '완납'}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 월별 청구 이력 */}
                    {modalBillingHistory.length > 0 && (
                      <div className="bg-white rounded border">
                        <div className="flex justify-between items-center px-3 pt-3 pb-2">
                          <div className="text-xs font-medium text-gray-700">월별 청구 이력 (VAT 포함)</div>
                        </div>
                        <div className="max-h-[280px] overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 text-left text-xs text-gray-500">월</th>
                                <th className="px-2 py-2 text-right text-xs text-gray-500">기본료</th>
                                <th className="px-2 py-2 text-center text-xs text-gray-500">알림톡</th>
                                <th className="px-2 py-2 text-right text-xs text-gray-500">청구(VAT)</th>
                                <th className="px-2 py-2 text-right text-xs text-gray-500">입금</th>
                                <th className="px-2 py-2 text-right text-xs text-gray-500">잔여</th>
                                <th className="px-2 py-2 text-center text-xs text-gray-500">상태</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {modalBillingHistory.map((m) => (
                                <tr key={m.year_month} className={m.outstanding > 0 ? 'bg-red-50' : ''}>
                                  <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{m.year_month}</td>
                                  <td className="px-2 py-2 text-right text-gray-600 whitespace-nowrap">
                                    {m.base_fee > 0 ? `${m.base_fee.toLocaleString()}` : '-'}
                                  </td>
                                  <td className="px-2 py-2 text-center whitespace-nowrap">
                                    {m.alimtalk_used > 0 ? (
                                      <span className="text-xs">
                                        <span className="text-gray-600">{m.alimtalk_used}</span>
                                        <span className="text-gray-400">/{m.alimtalk_free}건</span>
                                        {m.alimtalk_over > 0 && (
                                          <span className="text-red-500 ml-1">+{m.alimtalk_over}</span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-2 text-right text-gray-700 font-medium whitespace-nowrap">
                                    {m.billed > 0 ? `${m.billed.toLocaleString()}` : '-'}
                                  </td>
                                  <td className="px-2 py-2 text-right text-emerald-600 whitespace-nowrap">
                                    {(m.paid > 0 || m.carry_in > 0) ? (
                                      <span>
                                        {m.paid > 0 && m.paid.toLocaleString()}
                                        {m.carry_in > 0 && (
                                          <span className="text-blue-500 text-xs ml-1">
                                            {m.paid > 0 ? '+' : ''}{m.carry_in.toLocaleString()} 이월
                                          </span>
                                        )}
                                      </span>
                                    ) : '-'}
                                  </td>
                                  <td className={`px-2 py-2 text-right font-bold whitespace-nowrap ${m.outstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    {m.outstanding > 0 ? `${m.outstanding.toLocaleString()}` : '-'}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {m.billed === 0 ? (
                                      <span className="text-xs text-gray-400">-</span>
                                    ) : m.is_paid ? (
                                      <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">완납</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">미납</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 입금 이력 리스트 */}
                    <div>
                      <div className="text-xs font-medium text-emerald-700 mb-2">입금 내역</div>
                      {modalPayments.length === 0 ? (
                        <div className="text-center text-gray-400 py-3 text-sm">입금 이력이 없습니다.</div>
                      ) : (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {modalPayments.map((p) => (
                            <div key={p.id} className="flex items-center justify-between bg-white p-3 rounded border text-sm">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-emerald-700">{p.amount.toLocaleString()}원</span>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    p.payment_type === 'bank_transfer' ? 'bg-blue-100 text-blue-700' :
                                    p.payment_type === 'card' ? 'bg-purple-100 text-purple-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {p.payment_type === 'bank_transfer' ? '계좌이체' :
                                     p.payment_type === 'card' ? '카드' :
                                     p.payment_type === 'credit_offset' ? '크레딧' : p.payment_type}
                                  </span>
                                  <span className="text-gray-400">{p.year_month}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {new Date(p.paid_at).toLocaleString('ko-KR')}
                                  {p.notes && <span className="ml-2 text-gray-400">| {p.notes}</span>}
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeletePayment(p.id, detailData.organization_id)}
                                className="text-red-400 hover:text-red-600 text-xs ml-2"
                              >
                                삭제
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 계약 상세 (current_settings) */}
              {detailData.current_settings && Object.keys(detailData.current_settings).length > 0 && (
                <div className="bg-blue-50 p-4 rounded">
                  <h4 className="font-medium mb-2 text-blue-800">계약 정보</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">플랜:</span>
                      <span className="font-medium">{detailData.current_settings.plan_display_name || detailData.current_settings.plan_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">시작일:</span>
                      <span className="font-medium">{detailData.current_settings.start_date || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">종료일:</span>
                      <span className="font-medium">{detailData.current_settings.end_date || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">정가 (총액):</span>
                      <span className="font-medium">{detailData.current_settings.original_price?.toLocaleString() || '0'}원</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">실결제 (총액):</span>
                      <span className="font-medium text-blue-700">{detailData.current_settings.paid_amount?.toLocaleString() || '0'}원</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">월 실결제:</span>
                      <span className="font-medium text-green-700">{detailData.current_settings.monthly_fee?.toLocaleString() || '0'}원</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">할인율:</span>
                      <span className="font-medium text-orange-600">{detailData.current_settings.discount_rate || 0}%</span>
                    </div>
                    {detailData.current_settings.custom_alimtalk_free_quota != null && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-gray-600">알림톡 무료 건수:</span>
                        <span className="font-medium">{detailData.current_settings.custom_alimtalk_free_quota}건</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 플랜 제한 정보 */}
              <div className="bg-green-50 p-4 rounded">
                <h4 className="font-medium mb-2 text-green-800">플랜 제한</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">최대 학생 수:</span>
                    <span className="font-medium">{(detailData.subscription?.limits?.max_users ?? 0) >= 99999 ? '무제한' : `${detailData.subscription?.limits?.max_users ?? '-'}명`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">최대 게이트 수:</span>
                    <span className="font-medium">{(detailData.subscription?.limits?.max_gates ?? 0) >= 99999 ? '무제한' : `${detailData.subscription?.limits?.max_gates ?? '-'}개`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">최대 지점 수:</span>
                    <span className="font-medium">{(detailData.subscription?.limits?.max_branches ?? 0) >= 99999 ? '무제한' : `${detailData.subscription?.limits?.max_branches ?? '-'}개`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">알림톡 무료:</span>
                    <span className="font-medium">{(detailData.subscription?.limits?.alimtalk_monthly ?? 0) >= 99999 ? '무제한' : `${detailData.subscription?.limits?.alimtalk_monthly ?? 0}건`}</span>
                  </div>
                </div>
              </div>

              {/* 조직 ID (접힌 상태) */}
              <div className="text-xs text-gray-400 text-center">
                ID: {detailData.organization_id}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setDetailData(null);
                }}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 플랜 변경 모달 */}
      {showModal && selectedOrg && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowModal(false);
            setSelectedOrg(null);
            setCurrentSettings(null);
            setAlimtalkFreeQuota(null);
            setShowCalculation(false);
          }}
        >
          <div
            className="bg-white p-6 rounded-lg w-[500px] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">
              플랜 변경: {selectedOrg.name}
            </h3>

            {settingsLoading ? (
              <div className="text-center py-8 text-gray-500">설정값 로딩 중...</div>
            ) : (
            <div className="space-y-4">
              {/* 현재 설정 요약 */}
              {currentSettings && (
                <div className="bg-green-50 p-3 rounded text-sm border border-green-200">
                  <div className="font-medium text-green-800 mb-2">현재 적용된 설정</div>
                  <div className="grid grid-cols-2 gap-2 text-green-700">
                    <div>플랜: {currentSettings.plan_display_name || currentSettings.plan_name}</div>
                    <div>시작일: {currentSettings.start_date}</div>
                    <div>월 요금: {currentSettings.monthly_fee?.toLocaleString()}원</div>
                    {currentSettings.custom_alimtalk_free_quota !== null && (
                      <div className="col-span-2">알림톡 무료: {currentSettings.custom_alimtalk_free_quota}건</div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">플랜</label>
                <select
                  className="w-full px-3 py-2 border rounded"
                  id="plan"
                  value={selectedPlan || currentSettings?.plan_name || ''}
                  onChange={(e) => {
                    setSelectedPlan(e.target.value);
                    const foundPlan = plans.find(p => p.name.toUpperCase() === e.target.value);
                    if (foundPlan) {
                      setSelectedPlanPrice(foundPlan.price);
                    }
                  }}
                >
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.name.toUpperCase()}>
                      {plan.display_name} ({plan.price.toLocaleString()}원/월)
                    </option>
                  ))}
                </select>
              </div>

              {/* 시작일 입력 */}
              <div>
                <label className="block text-sm font-medium mb-2">시작일 (결제일)</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
                <p className="text-xs text-gray-500 mt-1">
                  * 매월 이 날짜에 청구됩니다 (예: 13일 → 매월 13일)
                </p>
              </div>


              {/* 요금 설정 */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <label className="block text-sm font-medium mb-3">요금 설정</label>

                {/* 요금 방식 선택 */}
                <div className="flex space-x-4 mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="discountMode"
                      checked={discountMode === 'rate'}
                      onChange={() => setDiscountMode('rate')}
                      className="mr-2"
                    />
                    <span className="text-sm">할인율 적용 (%)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="discountMode"
                      checked={discountMode === 'amount'}
                      onChange={() => setDiscountMode('amount')}
                      className="mr-2"
                    />
                    <span className="text-sm">월 요금 직접입력</span>
                  </label>
                </div>

                {/* 요금 입력 */}
                {discountMode === 'rate' ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={discountRate}
                      onChange={(e) => {
                        setDiscountRate(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)));
                        setShowCalculation(false);
                      }}
                      className="w-24 px-3 py-2 border rounded"
                      placeholder="0"
                    />
                    <span className="text-gray-600">%</span>
                    <button
                      type="button"
                      onClick={() => setShowCalculation(true)}
                      className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    >
                      계산하기
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">월</span>
                    <input
                      type="number"
                      min="0"
                      value={paidAmount}
                      onChange={(e) => {
                        setPaidAmount(Math.max(0, parseInt(e.target.value) || 0));
                        setShowCalculation(false);
                      }}
                      className="w-32 px-3 py-2 border rounded"
                      placeholder="월 요금"
                    />
                    <span className="text-gray-600">원</span>
                    <button
                      type="button"
                      onClick={() => setShowCalculation(true)}
                      className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    >
                      계산하기
                    </button>
                  </div>
                )}

                {/* 계산 결과 미리보기 */}
                {showCalculation && selectedPlanPrice > 0 && (
                  <div className="bg-white p-3 rounded border text-sm mt-3">
                    <div className="font-medium text-gray-800 mb-2">계산 결과</div>
                    <div className="text-gray-600">
                      기본 월 요금: <span className="font-medium">{selectedPlanPrice.toLocaleString()}원</span>
                    </div>
                    {discountMode === 'rate' ? (
                      <div className="text-green-700 font-medium">
                          할인 후 월 요금: {Math.round(selectedPlanPrice * (100 - discountRate) / 100).toLocaleString()}원
                          {discountRate > 0 && ` (${discountRate}% 할인)`}
                        </div>
                    ) : (
                      <div className="text-green-700 font-medium">
                          적용 월 요금: {paidAmount.toLocaleString()}원
                          {selectedPlanPrice > 0 && paidAmount < selectedPlanPrice &&
                            ` (${Math.round((1 - paidAmount / selectedPlanPrice) * 100)}% 할인)`
                          }
                        </div>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  * 플랜 기본 요금을 적용하려면 할인율 0% 또는 월 요금을 비워두세요
                </p>
              </div>

              {/* 알림톡 무료 제공량 설정 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <label className="block text-sm font-medium mb-3">알림톡 무료 제공량</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    value={alimtalkFreeQuota ?? currentSettings?.custom_alimtalk_free_quota ?? currentSettings?.default_alimtalk_free_quota ?? 1000}
                    onChange={(e) => setAlimtalkFreeQuota(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-32 px-3 py-2 border rounded"
                    placeholder="무료 건수"
                  />
                  <span className="text-gray-600">건/월</span>
                  {currentSettings?.default_alimtalk_free_quota && (
                    <span className="text-sm text-purple-600 ml-2">
                      (플랜 기본: {currentSettings.default_alimtalk_free_quota}건)
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  * 무료 제공량을 초과한 발송 건수에 대해 과금됩니다 (건당 13원)
                </p>
              </div>

              {/* 과금 방식 */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <label className="block text-sm font-medium mb-3">과금 방식</label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer ${billingType === 'postpaid' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    <input type="radio" name="billingType" value="postpaid" checked={billingType === 'postpaid'} onChange={() => setBillingType('postpaid')} />
                    <div>
                      <div className="font-medium text-sm">후불제</div>
                      <div className="text-xs text-gray-500">월말 사용량 정산</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer ${billingType === 'prepaid' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                    <input type="radio" name="billingType" value="prepaid" checked={billingType === 'prepaid'} onChange={() => setBillingType('prepaid')} />
                    <div>
                      <div className="font-medium text-sm">선불제</div>
                      <div className="text-xs text-gray-500">크레딧 충전 후 차감</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={() => {
                    const plan = (selectedPlan || currentSettings?.plan_name || 'PRO').toLowerCase() as 'starter' | 'basic' | 'pro' | 'premium';
                    handleUpdatePlan(selectedOrg.id, plan, 12, startDate);
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  변경 확인
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedOrg(null);
                    setCurrentSettings(null);
                    setAlimtalkFreeQuota(null);
                    setShowCalculation(false);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  취소
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* 플랜 신청 승인 모달 */}
      {showConfirmModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowConfirmModal(false);
            setSelectedRequest(null);
          }}
        >
          <div
            className="bg-white p-6 rounded-lg w-[450px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">플랜 신청 승인</h3>

            <div className="space-y-4">
              {/* 신청 정보 요약 */}
              <div className="bg-blue-50 p-4 rounded">
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-600">독서실:</span> <span className="font-medium">{selectedRequest.organization_name}</span></div>
                  <div><span className="text-gray-600">신청 플랜:</span> <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPlanBadgeColor(selectedRequest.requested_plan)}`}>{getPlanDisplayName(selectedRequest.requested_plan)}</span></div>
                  <div><span className="text-gray-600">입금자:</span> <span className="font-medium">{selectedRequest.depositor_name || '-'}</span></div>
                  <div><span className="text-gray-600">입금액:</span> <span className="font-medium">{selectedRequest.deposit_amount ? `${selectedRequest.deposit_amount.toLocaleString()}원` : '-'}</span></div>
                </div>
              </div>

              {/* 기간 설정 */}
              <div>
                <label className="block text-sm font-medium mb-2">시작일</label>
                <input
                  type="date"
                  value={confirmStartDate}
                  onChange={(e) => setConfirmStartDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>


              <div className="flex space-x-2 pt-2">
                <button
                  onClick={handleConfirmRequest}
                  disabled={processingRequest}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  {processingRequest ? '처리 중...' : '승인'}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedRequest(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 플랜 신청 거부 모달 */}
      {showRejectModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowRejectModal(false);
            setSelectedRequest(null);
            setRejectReason('');
          }}
        >
          <div
            className="bg-white p-6 rounded-lg w-[450px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">플랜 신청 거부</h3>

            <div className="space-y-4">
              {/* 신청 정보 요약 */}
              <div className="bg-red-50 p-4 rounded">
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-600">독서실:</span> <span className="font-medium">{selectedRequest.organization_name}</span></div>
                  <div><span className="text-gray-600">신청 플랜:</span> <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPlanBadgeColor(selectedRequest.requested_plan)}`}>{getPlanDisplayName(selectedRequest.requested_plan)}</span></div>
                </div>
              </div>

              {/* 거부 사유 입력 */}
              <div>
                <label className="block text-sm font-medium mb-2">거부 사유 (필수)</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="거부 사유를 입력해주세요..."
                  className="w-full px-3 py-2 border rounded h-24 resize-none"
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={handleRejectRequest}
                  disabled={processingRequest || !rejectReason.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
                >
                  {processingRequest ? '처리 중...' : '거부'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedRequest(null);
                    setRejectReason('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 동기화 API 키 관리 모달 */}
      {showSyncKeyModal && syncKeyOrg && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowSyncKeyModal(false);
            setSyncKeyOrg(null);
            setSyncKeyData(null);
          }}
        >
          <div
            className="bg-white p-6 rounded-lg w-[500px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">동기화 API 키 관리</h3>
              <button
                onClick={() => {
                  setShowSyncKeyModal(false);
                  setSyncKeyOrg(null);
                  setSyncKeyData(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                X
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">독서실: <span className="font-semibold text-gray-900">{syncKeyOrg.name}</span></p>
              <p className="text-xs text-gray-500">ID: {syncKeyOrg.id}</p>
            </div>

            {syncKeyLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* 현재 키 상태 + 연동 상태 */}
                <div className={`p-4 rounded-lg ${syncKeyData?.has_key ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${syncKeyData?.has_key ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="text-sm font-medium">
                        {syncKeyData?.has_key ? '키 발급됨' : '키 미발급'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        syncKeyData?.cloud_sync_connected ? 'bg-blue-500' : 'bg-gray-400'
                      }`} />
                      <span className="text-sm font-medium">
                        {syncKeyData?.cloud_sync_connected
                          ? '연동 완료'
                          : syncKeyData?.cloud_sync_enabled
                            ? '키 입력됨 (동기화 OFF)'
                            : '미연동'}
                      </span>
                    </div>
                  </div>
                  {syncKeyData?.sync_api_key && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">API 키:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-white px-2 py-1 rounded border flex-1 break-all font-mono">
                          {syncKeyData.sync_api_key}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(syncKeyData.sync_api_key || '');
                            alert('클립보드에 복사되었습니다');
                          }}
                          className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 whitespace-nowrap"
                        >
                          복사
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 안내 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  <p className="font-medium mb-1">사용 방법</p>
                  <p>1. 키를 발급하고 오너에게 전달합니다 (카톡/이메일).</p>
                  <p>2. 오너가 로컬 PC 웹 &gt; 설정 &gt; 클라우드 설정에서 키를 붙여넣고 저장합니다.</p>
                  <p>3. 오너가 동기화를 켜면 위 상태가 "연동 완료"로 변경됩니다.</p>
                  <p>4. 키를 폐기하면 해당 독서실의 동기화가 중단됩니다.</p>
                </div>

                {/* 배포 모드 설정 */}
                <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-indigo-800 mb-2">배포 모드</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={syncKeyOrg?.deploy_mode || ''}
                      onChange={async (e) => {
                        const mode = e.target.value;
                        if (!mode) return;
                        try {
                          await repository.setDeployMode(syncKeyOrg!.id, mode);
                          setSyncKeyOrg({ ...syncKeyOrg!, deploy_mode: mode as any });
                          alert(`배포 모드가 ${mode === 'local_ubuntu' ? 'Ubuntu' : 'Windows'}로 변경되었습니다`);
                          fetchAll();
                        } catch (err: any) {
                          alert('변경 실패: ' + (err?.response?.data?.detail || err?.message || ''));
                        }
                      }}
                      className="flex-1 text-sm border rounded px-2 py-1.5"
                    >
                      <option value="">미설정</option>
                      <option value="local_ubuntu">Ubuntu (Docker)</option>
                      <option value="local_windows">Windows (직접 실행)</option>
                    </select>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      syncKeyOrg?.deploy_mode === 'local_ubuntu' ? 'bg-orange-100 text-orange-700' :
                      syncKeyOrg?.deploy_mode === 'local_windows' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {syncKeyOrg?.deploy_mode === 'local_ubuntu' ? 'Ubuntu' :
                       syncKeyOrg?.deploy_mode === 'local_windows' ? 'Windows' : '미설정'}
                    </span>
                  </div>
                  <p className="text-xs text-indigo-600 mt-1">
                    * Ubuntu: Docker 기반 (newattend-ubuntu) / Windows: 직접 실행 (newattend-windows)
                  </p>
                </div>

                {/* 알림톡 로그 동기화 */}
                <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-orange-800 mb-2">알림톡 로그 동기화</p>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={localServerUrl}
                      onChange={(e) => setLocalServerUrl(e.target.value)}
                      placeholder="로컬 서버 URL (예: http://100.105.42.31)"
                      className="flex-1 text-xs border rounded px-2 py-1.5"
                    />
                    <button
                      onClick={async () => {
                        if (!localServerUrl.trim()) return alert('URL을 입력해주세요');
                        try {
                          await repository.setLocalServerUrl(syncKeyOrg!.id, localServerUrl.trim());
                          alert('로컬 서버 URL 저장 완료');
                        } catch (err: any) {
                          alert('저장 실패: ' + (err?.response?.data?.detail || err?.message || ''));
                        }
                      }}
                      className="text-xs bg-gray-200 px-3 py-1.5 rounded hover:bg-gray-300 whitespace-nowrap"
                    >
                      저장
                    </button>
                  </div>
                  <button
                    disabled={pullLoading}
                    onClick={async () => {
                      setPullLoading(true);
                      setPullResult(null);
                      try {
                        const result = await repository.pullNotificationLogs(syncKeyOrg!.id);
                        setPullResult(result.message || '동기화 완료');
                      } catch (err: any) {
                        setPullResult('실패: ' + (err?.response?.data?.detail || err?.message || ''));
                      } finally {
                        setPullLoading(false);
                      }
                    }}
                    className="w-full bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm font-medium disabled:opacity-50"
                  >
                    {pullLoading ? '동기화 중...' : '알림톡 로그 즉시 가져오기'}
                  </button>
                  {pullResult && (
                    <p className={`text-xs mt-2 ${pullResult.startsWith('실패') ? 'text-red-600' : 'text-green-600'}`}>
                      {pullResult}
                    </p>
                  )}
                  <p className="text-xs text-orange-600 mt-2">
                    * 자동: 로컬 서버에서 30분마다 자동 동기화됩니다
                  </p>
                </div>

                {/* 버튼 */}
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (syncKeyData?.has_key && !window.confirm('기존 키를 폐기하고 새 키를 발급하시겠습니까?')) return;
                      setSyncKeyLoading(true);
                      try {
                        const result = await repository.generateSyncKey(syncKeyOrg.id);
                        setSyncKeyData({ sync_api_key: result.sync_api_key, has_key: true });
                        alert(result.message);
                      } catch (err: any) {
                        alert('키 발급 실패: ' + (err?.response?.data?.detail || err?.message || ''));
                      } finally {
                        setSyncKeyLoading(false);
                      }
                    }}
                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm font-medium"
                  >
                    {syncKeyData?.has_key ? '키 재발급' : '키 발급'}
                  </button>
                  {syncKeyData?.has_key && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('API 키를 폐기하면 동기화가 중단됩니다. 계속하시겠습니까?')) return;
                        setSyncKeyLoading(true);
                        try {
                          const result = await repository.revokeSyncKey(syncKeyOrg.id);
                          setSyncKeyData({ sync_api_key: null, has_key: false });
                          alert(result.message);
                        } catch (err: any) {
                          alert('키 폐기 실패: ' + (err?.response?.data?.detail || err?.message || ''));
                        } finally {
                          setSyncKeyLoading(false);
                        }
                      }}
                      className="bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200 text-sm font-medium"
                    >
                      키 폐기
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
